<script setup lang="ts">
const props = defineProps<{
  donateToUsAmount: number;
  formatAmount: (amount: number) => string;
  organizationName?: string;
  /** Compteur −/+ en pilule (page aux boutons « pill »). */
  pilule?: boolean;
}>();

const emit = defineEmits<{
  "update:donateToUsAmount": [value: number];
}>();

const { t } = useI18n();

// Computed pour savoir si le soutien est activé
const isEnabled = computed(() => props.donateToUsAmount > 0);

// Texte dynamique selon le nom de l'organisation
const isMosquee = computed(() => {
  const name = (props.organizationName || "").toLowerCase();
  return (
    name.includes("masjid") ||
    name.includes("mosquée") ||
    name.includes("mosquee") ||
    name.includes("islam")
  );
});
const targetLabel = computed(() =>
  isMosquee.value ? t("don.soutenirDonoor.cibleMosquees") : t("don.soutenirDonoor.cibleAssociations"),
);
const targetSingulier = computed(() =>
  isMosquee.value
    ? t("don.soutenirDonoor.cibleMosqueeSingulier")
    : t("don.soutenirDonoor.cibleAssociationSingulier"),
);

// Step ASYMÉTRIQUE pour inciter au don : on AUGMENTE plus vite qu'on
// diminue (UX Donorbox/Patreon). Karim 2026-06-28.
//   <10€  : +1€  / -0.5€
//   10-30€: +2€  / -1€
//   30-100: +5€  / -2€
//   >=100€: +10€ / -5€
//
// Le pas de DIMINUTION à 0,50 € va jusqu'à 20 € et non 10 € (Karim
// 2026-09-12) : entre 10 et 20 €, descendre d'un euro entier faisait perdre
// 10 % du soutien d'un coup.
const incrementStep = computed(() => {
  const amount = props.donateToUsAmount;
  if (amount < 10) return 1;
  if (amount < 30) return 2;
  if (amount < 100) return 5;
  return 10;
});

const decrementStep = computed(() => {
  const amount = props.donateToUsAmount;
  if (amount <= 20) return 0.5;
  if (amount <= 30) return 1;
  if (amount <= 100) return 2;
  return 5;
});

const round = (n: number) => Math.round(n * 100) / 100;

const decrement = () => {
  emit(
    "update:donateToUsAmount",
    Math.max(0, round(props.donateToUsAmount - decrementStep.value)),
  );
};

const increment = () => {
  if (props.donateToUsAmount === 0) {
    emit("update:donateToUsAmount", 2);
  } else {
    emit(
      "update:donateToUsAmount",
      round(props.donateToUsAmount + incrementStep.value),
    );
  }
};
</script>

<template>
  <div class="transition-all duration-200">
    <!-- Ligne label + input -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <div
          class="donoor-icon-wrap"
          :class="isEnabled ? 'is-happy' : 'is-sad'"
        >
          <svg viewBox="0 0 28 28" fill="none" class="donoor-icon">
            <!-- Heart shape (visible when enabled) -->
            <!--
              Couleur pilotée par la CLASSE du parent (`is-happy` / `is-sad`),
              pas par un `:fill` lié.

              🔴 POURQUOI (Karim 2026-09-22). Avec `:fill` + `transition: fill`,
              le cœur restait GRIS quand la valeur était déjà bonne AU PREMIER
              RENDU : le navigateur peint l'état initial, et sans changement
              ultérieur la transition ne se déclenche jamais. Le cas se produit
              sur le lien de paiement, dont le pourboire est connu dès le
              montage — alors que sur /a/ et /b/ il passe de 0 à 3 après coup,
              ce qui masquait le défaut.

              `fill` reste posé en attribut comme SECOURS (navigateur sans CSS,
              capture d'image) ; la règle CSS l'emporte.
            -->
            <path
              class="donoor-heart"
              d="M14 24s-9-5.5-9-12c0-3.3 2.7-6 6-6 1.8 0 3 .8 3 .8s1.2-.8 3-.8c3.3 0 6 2.7 6 6 0 6.5-9 12-9 12z"
              fill="#d1d5db"
            />
            <!-- Eyes (always visible) -->
            <circle class="donoor-eye-l" cx="11" cy="12" r="1.2" fill="white" />
            <circle class="donoor-eye-r" cx="17" cy="12" r="1.2" fill="white" />
            <!-- Happy mouth (when enabled) -->
            <path
              v-if="isEnabled"
              class="donoor-mouth-happy"
              d="M11.5 16c0 0 1.5 2 2.5 2s2.5-2 2.5-2"
              stroke="white"
              stroke-width="1.2"
              stroke-linecap="round"
              fill="none"
            />
            <!-- Sad mouth (when disabled) -->
            <path
              v-else
              class="donoor-mouth-sad"
              d="M11.5 17.5c0 0 1.5-1.5 2.5-1.5s2.5 1.5 2.5 1.5"
              stroke="white"
              stroke-width="1.2"
              stroke-linecap="round"
              fill="none"
            />
            <!-- Tear drop (when disabled) -->
            <path
              v-if="!isEnabled"
              class="donoor-tear"
              d="M18.5 14c0 0 .5 1.5 .5 2.2 0 .7-.5 1.3-1 1.3s-1-.6-1-1.3c0-.7.5-2.2.5-2.2z"
              fill="#60a5fa"
            />
          </svg>
        </div>
        <span class="text-sm text-gray-700 font-medium">
          {{ t('don.soutenirDonoor.titre') }}
        </span>
        <!-- UTooltip (Nuxt UI / Reka) téléporte son contenu → hydration
             mismatch SSR/client qui, sur cette page, CASSAIT l'hydratation du
             sous-arbre (le onMounted d'init Stripe ne s'exécutait pas → bouton
             "Faire un don" jamais payable). Un tooltip n'a aucune valeur au SSR
             (pas d'interaction sans JS) → on le rend client-only avec un
             fallback = l'icône info statique (même rendu visuel au 1er paint). -->
        <ClientOnly>
          <UTooltip
            :delay-duration="0"
            :content="{
              side: 'top',
              align: 'end',
              alignOffset: -100,
              sideOffset: 12,
            }"
            :ui="{
              content:
                '!h-auto bg-white text-gray-700 shadow-lg border border-gray-200 rounded-lg p-3 text-xs w-52 whitespace-normal ring-0',
            }"
            :open-on-click="true"
          >
            <div @click.stop @touchstart.stop>
              <UIcon
                name="i-lucide-info"
                class="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors"
              />
            </div>
            <template #content>
              <!-- Textes revus avec Karim le 2026-09-26 : « nos
                   associations » (plus « à travers le monde »), Donoor est
                   100 % gratuit pour elles et le soutien est son SEUL
                   financement. -->
              <strong>
                {{ t('don.soutenirDonoor.infobulle', { cible: targetLabel }) }}
              </strong>
            </template>
          </UTooltip>
          <template #fallback>
            <UIcon
              name="i-lucide-info"
              class="w-4 h-4 text-gray-400 cursor-help"
            />
          </template>
        </ClientOnly>
      </div>

      <!-- Input montant avec +/- sur la même ligne -->
      <div class="flex items-center gap-1">
        <div class="flex items-center">
          <button
            type="button"
            class="w-7 h-7 flex items-center justify-center bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors"
            :class="pilule ? 'rounded-l-full pl-0.5' : 'rounded-l-md'"
            :aria-label="t('don.soutenirDonoor.diminuerAria')"
            @click="decrement"
          >
            <span class="text-sm font-bold">−</span>
          </button>
          <span
            class="w-12 h-7 px-1 text-base text-center bg-white border-y border-gray-200 text-gray-900 flex items-center justify-center select-none"
            >{{ donateToUsAmount }}</span
          >
          <button
            type="button"
            class="w-7 h-7 flex items-center justify-center bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors"
            :class="pilule ? 'rounded-r-full pr-0.5' : 'rounded-r-md'"
            :aria-label="t('don.soutenirDonoor.augmenterAria')"
            @click="increment"
          >
            <span class="text-sm font-bold">+</span>
          </button>
        </div>
        <span class="text-sm text-gray-600">€</span>
      </div>
    </div>

    <!-- Description (visible si non activé) avec animation smooth -->
    <div
      class="grid transition-all duration-300 ease-in-out"
      :class="
        isEnabled ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'
      "
    >
      <div class="overflow-hidden">
        <!-- Voix de l'ASSOCIATION, comme « Couvrir les frais » (Karim
             2026-09-26) : c'est elle que le payeur vient soutenir, c'est elle
             qui dit pourquoi Donoor compte. Seuls les mots clés en gras. -->
        <i18n-t keypath="don.soutenirDonoor.description" tag="p" class="text-xs text-gray-500 mt-2">
          <template #cible>{{ targetSingulier }}</template>
          <template #gratuit><strong class="font-semibold text-gray-700">{{ t('don.soutenirDonoor.descriptionGratuitement') }}</strong></template>
          <template #seulMoyen><strong class="font-semibold text-gray-700">{{ t('don.soutenirDonoor.descriptionSeulMoyen') }}</strong></template>
          <template #serviceGratuit><strong class="font-semibold text-gray-700">{{ t('don.soutenirDonoor.descriptionServiceGratuit') }}</strong></template>
        </i18n-t>
      </div>
    </div>
  </div>
</template>

<style scoped>
.donoor-icon-wrap {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.donoor-icon {
  width: 100%;
  height: 100%;
  overflow: visible;
}

/* Heart */
.donoor-heart {
  transition:
    fill 0.4s ease,
    transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-origin: center 14px;
}
/*
 * La couleur vient d'ICI et non d'un `:fill` lié : une valeur déjà correcte au
 * premier rendu n'anime aucune transition, et le cœur restait gris.
 */
.is-happy .donoor-heart {
  fill: #ef4444;
  animation: heartbeat 1.2s ease-in-out infinite;
}
.is-sad .donoor-heart {
  fill: #d1d5db;
  transform: scale(0.95);
}

/* Eyes */
.donoor-eye-l,
.donoor-eye-r {
  transition:
    transform 0.3s ease,
    cy 0.3s ease;
}
.is-sad .donoor-eye-l,
.is-sad .donoor-eye-r {
  transform: scaleY(1.3);
}

/* Mouths */
.donoor-mouth-happy {
  animation: smileIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.donoor-mouth-sad {
  animation: sadIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Tear */
.donoor-tear {
  animation: tearDrop 1.5s ease-in-out infinite;
  transform-origin: center top;
}

@keyframes heartbeat {
  0%,
  100% {
    transform: scale(1);
  }
  15% {
    transform: scale(1.12);
  }
  30% {
    transform: scale(1);
  }
  45% {
    transform: scale(1.08);
  }
  60% {
    transform: scale(1);
  }
}

@keyframes smileIn {
  0% {
    opacity: 0;
    transform: scaleX(0);
  }
  100% {
    opacity: 1;
    transform: scaleX(1);
  }
}

@keyframes sadIn {
  0% {
    opacity: 0;
    transform: translateY(-2px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes tearDrop {
  0%,
  100% {
    opacity: 0;
    transform: translateY(-2px) scale(0.8);
  }
  30% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  70% {
    opacity: 1;
    transform: translateY(1px) scale(1);
  }
  90% {
    opacity: 0;
    transform: translateY(3px) scale(0.6);
  }
}
</style>
